import type {RefObject} from 'react';
import {useCallback, useEffect, useState} from 'react';

/** Scroll positions within this many pixels of an edge count as being at it. */
const SCROLL_TOLERANCE = 2;

interface HorizontalScrollSteps {
  canScrollPrev: boolean;
  canScrollNext: boolean;
  scrollToPrev: () => void;
  scrollToNext: () => void;
  /** Give this to the scroll container's onScroll so the buttons keep up with a swipe or a wheel. */
  onContainerScroll: () => void;
}

/**
 * Steps a horizontally scrolling row one viewport at a time, landing on an item boundary. It also
 * reports whether either direction has anywhere left to go, since a mouse has only the buttons.
 */
export const useHorizontalScrollSteps = (containerRef: RefObject<HTMLElement | null>): HorizontalScrollSteps => {
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onContainerScroll = useCallback((): void => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const maxScroll = container.scrollWidth - container.clientWidth;
    setCanScrollPrev(container.scrollLeft > SCROLL_TOLERANCE);
    setCanScrollNext(container.scrollLeft < maxScroll - SCROLL_TOLERANCE);
  }, [containerRef]);

  /** Where the item that starts at or before this position begins, so a step never cuts one in half. */
  const getItemStartAtOrBefore = useCallback(
    (position: number): number => {
      const container = containerRef.current;

      if (!container) {
        return 0;
      }

      let start = 0;

      for (const child of container.children) {
        if (!(child instanceof HTMLElement)) {
          continue;
        }

        if (child.offsetLeft > position + SCROLL_TOLERANCE) {
          break;
        }

        start = child.offsetLeft;
      }

      return start;
    },
    [containerRef]
  );

  const scrollToNext = useCallback((): void => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const maxScroll = container.scrollWidth - container.clientWidth;
    let target = getItemStartAtOrBefore(container.scrollLeft + container.clientWidth);

    // An item wider than the viewport would leave the target on the current item; step forward a
    // viewport so the row never stalls.
    if (target <= container.scrollLeft + SCROLL_TOLERANCE) {
      target = container.scrollLeft + container.clientWidth;
    }

    container.scrollTo({left: Math.min(target, maxScroll), top: 0, behavior: 'smooth'});
  }, [containerRef, getItemStartAtOrBefore]);

  const scrollToPrev = useCallback((): void => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    let target = getItemStartAtOrBefore(container.scrollLeft - container.clientWidth);

    // Guarantee backward progress when the current item is wider than the viewport.
    if (target >= container.scrollLeft - SCROLL_TOLERANCE) {
      target = container.scrollLeft - container.clientWidth;
    }

    container.scrollTo({left: Math.max(target, 0), top: 0, behavior: 'smooth'});
  }, [containerRef, getItemStartAtOrBefore]);

  useEffect((): (() => void) | undefined => {
    onContainerScroll();
    const container = containerRef.current;

    if (!container || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(onContainerScroll);
    resizeObserver.observe(container);

    return (): void => {
      resizeObserver.disconnect();
    };
  }, [containerRef, onContainerScroll]);

  return {canScrollPrev, canScrollNext, scrollToPrev, scrollToNext, onContainerScroll};
};
